import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ClipboardList,
  Plus,
  Search,
  BarChart3,
  Users,
  MoreVertical,
  Edit,
  Trash2,
  Share2,
  Eye,
  LogOut,
  User,
  Settings,
  ChevronDown,
  Activity,
  Brain,
  FileText,
  Clock,
  Target,
  Zap,
  Download,
  Bell,
  HelpCircle,
  LayoutGrid,
  List,
  ArrowUpRight,
  Sparkles,
  Loader2,
  Menu,
  X,
  UserPlus,
  Copy,
  Check
} from 'lucide-react';
import { authAPI, surveysAPI, dashboardAPI, usersAPI } from '../services/api';
import { MobileNavAdmin } from '../components/MobileNav';

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal states
  const [showCreateSurvey, setShowCreateSurvey] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditSurvey, setShowEditSurvey] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Form states
  const [surveyForm, setSurveyForm] = useState({
    title: '',
    description: '',
    type: 'kap',
    target_responses: 100,
    status: 'draft'
  });
  
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    name: '',
    organization: '',
    role: 'researcher',
    password: ''
  });

  useEffect(() => {
    loadData();
  }, [navigate]);

  // Check for newSurvey URL param to auto-open create dialog
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('newSurvey') === 'true') {
      setShowCreateSurvey(true);
      // Clean up URL
      navigate('/dashboard', { replace: true });
    }
  }, [location.search, navigate]);

  const loadData = async () => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const token = localStorage.getItem('token');
    
    if (!isAuthenticated || !token) {
      navigate('/auth');
      return;
    }
    
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      const surveysData = await surveysAPI.getAll();
      setSurveys(surveysData);
      
      const statsData = await dashboardAPI.getStats();
      setStats(statsData);
      
      const activityData = await dashboardAPI.getActivity(10);
      setActivities(activityData);
      
    } catch (error) {
      console.error('Error loading dashboard:', error);
      if (error.response?.status === 401) {
        navigate('/auth');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await authAPI.logout();
    navigate('/auth');
  };

  const filteredSurveys = surveys.filter(survey => {
    const matchesSearch = survey.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      survey.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || survey.type === activeTab;
    return matchesSearch && matchesTab;
  });

  const handleCreateSurvey = async () => {
    if (!surveyForm.title.trim()) return;
    
    setIsSubmitting(true);
    try {
      const newSurvey = await surveysAPI.create(surveyForm);
      setSurveys([newSurvey, ...surveys]);
      setShowCreateSurvey(false);
      setSurveyForm({
        title: '',
        description: '',
        type: 'kap',
        target_responses: 100,
        status: 'draft'
      });
      // Refresh stats
      const statsData = await dashboardAPI.getStats();
      setStats(statsData);
      const activityData = await dashboardAPI.getActivity(10);
      setActivities(activityData);
    } catch (error) {
      console.error('Error creating survey:', error);
      alert('Failed to create survey: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSurvey = async () => {
    if (!selectedSurvey || !surveyForm.title.trim()) return;
    
    setIsSubmitting(true);
    try {
      const updated = await surveysAPI.update(selectedSurvey.id, surveyForm);
      setSurveys(surveys.map(s => s.id === selectedSurvey.id ? updated : s));
      setShowEditSurvey(false);
      setSelectedSurvey(null);
      // Refresh stats
      const statsData = await dashboardAPI.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error updating survey:', error);
      alert('Failed to update survey: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSurvey = async (id) => {
    if (!window.confirm('Are you sure you want to delete this survey?')) return;
    
    try {
      await surveysAPI.delete(id);
      setSurveys(surveys.filter(s => s.id !== id));
      // Refresh stats
      const statsData = await dashboardAPI.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error deleting survey:', error);
      alert('Failed to delete survey: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.username.trim() || !userForm.email.trim() || !userForm.password.trim()) {
      alert('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await usersAPI.create(userForm);
      setShowCreateUser(false);
      setUserForm({
        username: '',
        email: '',
        name: '',
        organization: '',
        role: 'researcher',
        password: ''
      });
      alert('User created successfully!');
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditSurvey = (survey) => {
    setSelectedSurvey(survey);
    setSurveyForm({
      title: survey.title,
      description: survey.description,
      type: survey.type,
      target_responses: survey.target_responses,
      status: survey.status
    });
    setShowEditSurvey(true);
  };

  const openShareDialog = (survey) => {
    setSelectedSurvey(survey);
    setShowShareDialog(true);
  };

  const handleExportReport = async (survey) => {
    try {
      const blob = await surveysAPI.exportReport(survey.id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `survey_report_${survey.title.replace(/\s+/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report');
    }
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/survey/${selectedSurvey?.id}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openCreateSurveyWithType = (type) => {
    setSurveyForm({
      ...surveyForm,
      type: type
    });
    setShowCreateSurvey(true);
  };

  const getTypeColor = (type) => {
    const colors = {
      kap: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      mrs: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      patient: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      hcp: 'bg-amber-500/10 text-amber-400 border-amber-500/20'
    };
    return colors[type] || 'bg-slate-500/10 text-slate-400';
  };

  const getTypeName = (type) => {
    const names = {
      kap: 'KAP Survey',
      mrs: 'Market Research',
      patient: 'Patient Survey',
      hcp: 'HCP Feedback'
    };
    return names[type] || type;
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-emerald-500';
    if (percentage >= 50) return 'bg-blue-500';
    if (percentage >= 25) return 'bg-amber-500';
    return 'bg-slate-500';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  const statsConfig = [
    {
      title: 'Active Studies',
      key: 'active_studies',
      icon: Activity,
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Total Responses',
      key: 'total_responses',
      icon: Users,
      gradient: 'from-emerald-500 to-teal-500'
    },
    {
      title: 'Completion Rate',
      key: 'completion_rate',
      icon: Target,
      gradient: 'from-purple-500 to-pink-500',
      suffix: '%'
    },
    {
      title: 'Avg Response Time',
      key: 'avg_response_time',
      icon: Clock,
      gradient: 'from-amber-500 to-orange-500'
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const isAdmin = user.role === 'administrator';

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-900 border-r border-slate-800 z-40 hidden lg:flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          <button onClick={() => navigate('/dashboard')} className="w-full flex items-center gap-3 px-4 py-3 text-white bg-white/10 rounded-xl font-medium">
            <LayoutGrid className="w-5 h-5" />
            Dashboard
          </button>
          <button onClick={() => navigate('/surveys')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
            <ClipboardList className="w-5 h-5" />
            Surveys
          </button>
          <button onClick={() => navigate('/analytics')} className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors">
            <BarChart3 className="w-5 h-5" />
            Analytics
          </button>
          {isAdmin && (
            <button 
              onClick={() => navigate('/users')}
              className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
            >
              <UserPlus className="w-5 h-5" />
              Manage Users
            </button>
          )}
        </nav>

        {/* Quick Actions */}
        <div className="p-4 border-t border-slate-800">
          <Button 
            onClick={() => setShowCreateSurvey(true)}
            className="w-full bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white gap-2 rounded-xl shadow-lg shadow-blue-500/20"
          >
            <Plus className="w-4 h-4" />
            New Survey
          </Button>
        </div>

        {/* User */}
        <div className="p-4 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-semibold">
                  {user.name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
                  <p className="text-xs text-slate-500 truncate">{isAdmin ? 'Administrator' : user.organization || user.role}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
              <DropdownMenuItem className="gap-2 cursor-pointer text-slate-300 focus:text-white focus:bg-white/10">
                <User className="w-4 h-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2 cursor-pointer text-slate-300 focus:text-white focus:bg-white/10">
                <Settings className="w-4 h-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem 
                className="gap-2 cursor-pointer text-red-400 focus:text-red-400 focus:bg-red-500/10"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Mobile Logo */}
              <div className="lg:hidden flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold text-white">KAP<span className="text-cyan-400">Surv</span></span>
              </div>

              {/* Search */}
              <div className="hidden md:flex flex-1 max-w-md">
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search surveys, templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-10 bg-slate-900 border-slate-800 text-white placeholder:text-slate-500 focus:border-cyan-500 focus:ring-cyan-500/20 rounded-xl"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                {isAdmin && (
                  <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20">
                    Admin
                  </Badge>
                )}
                <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white hover:bg-white/10 rounded-xl">
                  <Bell className="w-5 h-5" />
                </Button>
                <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white hover:bg-white/10 rounded-xl">
                  <HelpCircle className="w-5 h-5" />
                </Button>
                <div className="lg:hidden">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-semibold">
                        {user.name?.charAt(0) || 'U'}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-800">
                      <div className="px-3 py-2">
                        <p className="text-sm font-medium text-white">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                      <DropdownMenuSeparator className="bg-slate-800" />
                      <DropdownMenuItem 
                        className="gap-2 cursor-pointer text-red-400 focus:text-red-400 focus:bg-red-500/10"
                        onClick={handleLogout}
                      >
                        <LogOut className="w-4 h-4" />
                        Log out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          {/* Welcome */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-white">Welcome back, {user.name?.split(' ')[0] || 'Researcher'}!</h1>
              <p className="text-slate-400 mt-1">Here's your healthcare research overview for today.</p>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => setShowCreateSurvey(true)}
                className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white gap-2 rounded-xl lg:hidden"
              >
                <Plus className="w-4 h-4" />
                New Survey
              </Button>
              <Button variant="outline" className="gap-2 bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white rounded-xl">
                <Download className="w-4 h-4" />
                Export Report
              </Button>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {statsConfig.map((stat, index) => (
                <Card key={index} className="bg-slate-900 border-slate-800 overflow-hidden group hover:border-slate-700 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-slate-400">{stat.title}</p>
                        <p className="text-3xl font-bold text-white mt-2">
                          {stat.key === 'total_responses' 
                            ? stats[stat.key]?.toLocaleString() 
                            : stats[stat.key]}{stat.suffix || ''}
                        </p>
                        <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                          <ArrowUpRight className="w-3 h-3" />
                          Trending up
                        </p>
                      </div>
                      <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg opacity-80 group-hover:opacity-100 transition-opacity`}>
                        <stat.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Main Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Surveys List */}
            <div className="xl:col-span-2">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <CardTitle className="text-lg text-white">Your Surveys</CardTitle>
                      <CardDescription className="text-slate-500">Manage KAP and Market Research studies</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          placeholder="Search..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9 h-9 w-40 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 rounded-lg text-sm"
                        />
                      </div>
                      <div className="flex items-center bg-slate-800 rounded-lg p-1">
                        <button 
                          onClick={() => setViewMode('list')}
                          className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-white'}`}
                        >
                          <List className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => setViewMode('grid')}
                          className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-white'}`}
                        >
                          <LayoutGrid className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
                    <TabsList className="bg-slate-800 p-1 rounded-lg">
                      <TabsTrigger value="all" className="rounded-md text-sm data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">All</TabsTrigger>
                      <TabsTrigger value="kap" className="rounded-md text-sm data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">KAP</TabsTrigger>
                      <TabsTrigger value="mrs" className="rounded-md text-sm data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">Market Research</TabsTrigger>
                      <TabsTrigger value="hcp" className="rounded-md text-sm data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400">HCP</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {filteredSurveys.length === 0 ? (
                      <div className="text-center py-12">
                        <ClipboardList className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                        <p className="text-slate-500 mb-4">No surveys found</p>
                        <Button 
                          onClick={() => setShowCreateSurvey(true)}
                          className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white gap-2 rounded-xl"
                        >
                          <Plus className="w-4 h-4" />
                          Create your first survey
                        </Button>
                      </div>
                    ) : (
                      filteredSurveys.map((survey) => (
                        <div
                          key={survey.id}
                          className="p-4 rounded-xl bg-slate-800/50 border border-slate-800 hover:border-slate-700 transition-all group"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-medium text-white truncate">{survey.title}</h3>
                                <Badge 
                                  variant="outline"
                                  className={`${getTypeColor(survey.type)} text-xs font-medium border`}
                                >
                                  {getTypeName(survey.type)}
                                </Badge>
                                {survey.status === 'active' && (
                                  <span className="flex items-center gap-1 text-xs text-emerald-400">
                                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                                    Live
                                  </span>
                                )}
                                {survey.status === 'draft' && (
                                  <span className="text-xs text-slate-500">Draft</span>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 truncate mb-3">{survey.description}</p>
                              
                              {/* Progress */}
                              <div className="flex items-center gap-4">
                                <div className="flex-1 max-w-[200px]">
                                  <div className="flex items-center justify-between text-xs mb-1.5">
                                    <span className="text-slate-500">{survey.responses} / {survey.target_responses} responses</span>
                                    <span className="font-medium text-white">{Math.round(survey.completion_rate)}%</span>
                                  </div>
                                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full ${getProgressColor(survey.completion_rate)} rounded-full transition-all`}
                                      style={{ width: `${Math.min(100, survey.completion_rate)}%` }}
                                    />
                                  </div>
                                </div>
                                <span className="text-xs text-slate-600">Created {formatDate(survey.created_at)}</span>
                              </div>
                            </div>

                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
                                onClick={() => openEditSurvey(survey)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
                                onClick={() => openShareDialog(survey)}
                              >
                                <Share2 className="w-4 h-4" />
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                                  <DropdownMenuItem 
                                    className="gap-2 cursor-pointer text-slate-300 focus:text-white focus:bg-white/10"
                                    onClick={() => openEditSurvey(survey)}
                                  >
                                    <Edit className="w-4 h-4" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="gap-2 cursor-pointer text-slate-300 focus:text-white focus:bg-white/10"
                                    onClick={() => openShareDialog(survey)}
                                  >
                                    <Share2 className="w-4 h-4" />
                                    Share
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="gap-2 cursor-pointer text-slate-300 focus:text-white focus:bg-white/10"
                                    onClick={() => navigate(`/surveys/${survey.id}/analytics`)}
                                  >
                                    <BarChart3 className="w-4 h-4" />
                                    Analytics
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="gap-2 cursor-pointer text-slate-300 focus:text-white focus:bg-white/10"
                                    onClick={() => handleExportReport(survey)}
                                  >
                                    <Download className="w-4 h-4" />
                                    Export
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-slate-800" />
                                  <DropdownMenuItem 
                                    className="gap-2 cursor-pointer text-red-400 focus:text-red-400 focus:bg-red-500/10"
                                    onClick={() => handleDeleteSurvey(survey.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base text-white flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-400" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-3 h-11 bg-transparent border-slate-700 text-slate-300 hover:bg-blue-500/10 hover:text-blue-400 hover:border-blue-500/30 rounded-xl"
                    onClick={() => openCreateSurveyWithType('kap')}
                  >
                    <Brain className="w-4 h-4" />
                    Create KAP Survey
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start gap-3 h-11 bg-transparent border-slate-700 text-slate-300 hover:bg-emerald-500/10 hover:text-emerald-400 hover:border-emerald-500/30 rounded-xl"
                    onClick={() => openCreateSurveyWithType('mrs')}
                  >
                    <BarChart3 className="w-4 h-4" />
                    New Market Research
                  </Button>
                  {isAdmin && (
                    <Button 
                      variant="outline" 
                      className="w-full justify-start gap-3 h-11 bg-transparent border-slate-700 text-slate-300 hover:bg-amber-500/10 hover:text-amber-400 hover:border-amber-500/30 rounded-xl"
                      onClick={() => setShowCreateUser(true)}
                    >
                      <UserPlus className="w-4 h-4" />
                      Add New User
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base text-white flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-cyan-400" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {activities.length === 0 ? (
                      <p className="text-slate-500 text-sm text-center py-4">No recent activity</p>
                    ) : (
                      activities.map((activity) => (
                        <div key={activity.id} className="flex items-start gap-3">
                          <div className="w-2 h-2 rounded-full bg-cyan-400 mt-2 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-300">
                              <span className="font-medium">{activity.action}</span>
                            </p>
                            <p className="text-xs text-slate-500 truncate">{activity.survey_title}</p>
                            <p className="text-xs text-slate-600 mt-0.5">{formatTimeAgo(activity.timestamp)}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Help Card */}
              <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <HelpCircle className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white mb-1">Need Help?</h3>
                      <p className="text-sm text-slate-400 mb-3">Check our documentation or contact support for assistance.</p>
                      <Button size="sm" variant="outline" className="bg-transparent border-blue-500/30 text-blue-400 hover:bg-blue-500/10 rounded-lg">
                        View Docs
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>

      {/* Create Survey Dialog */}
      <Dialog open={showCreateSurvey} onOpenChange={setShowCreateSurvey}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Create New Survey</DialogTitle>
            <DialogDescription className="text-slate-400">
              Fill in the details to create a new KAP or Market Research survey.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Survey Title *</Label>
              <Input
                placeholder="e.g., COVID-19 Vaccine Hesitancy Study"
                value={surveyForm.title}
                onChange={(e) => setSurveyForm({...surveyForm, title: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <Textarea
                placeholder="Describe the purpose and scope of your survey..."
                value={surveyForm.description}
                onChange={(e) => setSurveyForm({...surveyForm, description: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 min-h-[100px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Survey Type</Label>
                <Select value={surveyForm.type} onValueChange={(v) => setSurveyForm({...surveyForm, type: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white focus:border-cyan-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="kap">KAP Survey</SelectItem>
                    <SelectItem value="mrs">Market Research</SelectItem>
                    <SelectItem value="patient">Patient Survey</SelectItem>
                    <SelectItem value="hcp">HCP Feedback</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Target Responses</Label>
                <Input
                  type="number"
                  value={surveyForm.target_responses}
                  onChange={(e) => setSurveyForm({...surveyForm, target_responses: parseInt(e.target.value) || 100})}
                  className="bg-slate-800 border-slate-700 text-white focus:border-cyan-500"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Status</Label>
              <Select value={surveyForm.status} onValueChange={(v) => setSurveyForm({...surveyForm, status: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white focus:border-cyan-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active (Live)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSurvey(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateSurvey} 
              disabled={isSubmitting || !surveyForm.title.trim()}
              className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Survey
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Survey Dialog */}
      <Dialog open={showEditSurvey} onOpenChange={setShowEditSurvey}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Edit Survey</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update the survey details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Survey Title *</Label>
              <Input
                value={surveyForm.title}
                onChange={(e) => setSurveyForm({...surveyForm, title: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white focus:border-cyan-500"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={surveyForm.description}
                onChange={(e) => setSurveyForm({...surveyForm, description: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white focus:border-cyan-500 min-h-[100px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Survey Type</Label>
                <Select value={surveyForm.type} onValueChange={(v) => setSurveyForm({...surveyForm, type: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white focus:border-cyan-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="kap">KAP Survey</SelectItem>
                    <SelectItem value="mrs">Market Research</SelectItem>
                    <SelectItem value="patient">Patient Survey</SelectItem>
                    <SelectItem value="hcp">HCP Feedback</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Target Responses</Label>
                <Input
                  type="number"
                  value={surveyForm.target_responses}
                  onChange={(e) => setSurveyForm({...surveyForm, target_responses: parseInt(e.target.value) || 100})}
                  className="bg-slate-800 border-slate-700 text-white focus:border-cyan-500"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Status</Label>
              <Select value={surveyForm.status} onValueChange={(v) => setSurveyForm({...surveyForm, status: v})}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white focus:border-cyan-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active (Live)</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSurvey(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateSurvey} 
              disabled={isSubmitting || !surveyForm.title.trim()}
              className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog (Admin Only) */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Create New User</DialogTitle>
            <DialogDescription className="text-slate-400">
              Add a new user to the platform. They will receive login credentials.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Full Name *</Label>
                <Input
                  placeholder="Dr. John Smith"
                  value={userForm.name}
                  onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Username *</Label>
                <Input
                  placeholder="johnsmith"
                  value={userForm.username}
                  onChange={(e) => setUserForm({...userForm, username: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                placeholder="john@hospital.org"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Organization</Label>
              <Input
                placeholder="Hospital or Research Institute"
                value={userForm.organization}
                onChange={(e) => setUserForm({...userForm, organization: e.target.value})}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Role</Label>
                <Select value={userForm.role} onValueChange={(v) => setUserForm({...userForm, role: v})}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white focus:border-cyan-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="researcher">Researcher</SelectItem>
                    <SelectItem value="physician">Physician</SelectItem>
                    <SelectItem value="analyst">Market Analyst</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Password *</Label>
                <Input
                  type="password"
                  placeholder="Min 6 characters"
                  value={userForm.password}
                  onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateUser} 
              disabled={isSubmitting || !userForm.username.trim() || !userForm.email.trim() || !userForm.password.trim()}
              className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Share Survey</DialogTitle>
            <DialogDescription className="text-slate-400">
              Share this survey with respondents.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Survey Link</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={`${window.location.origin}/survey/${selectedSurvey?.id}`}
                  className="bg-slate-800 border-slate-700 text-white"
                />
                <Button 
                  onClick={copyShareLink}
                  className="bg-slate-800 hover:bg-slate-700 text-white"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="p-4 bg-slate-800 rounded-lg">
              <h4 className="font-medium text-white mb-2">{selectedSurvey?.title}</h4>
              <p className="text-sm text-slate-400">{selectedSurvey?.description}</p>
              <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                <span>{selectedSurvey?.responses} responses</span>
                <span>{selectedSurvey?.target_responses} target</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)} className="border-slate-700 text-slate-300 hover:bg-slate-800">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Bottom Navigation */}
      <MobileNavAdmin />
      
      {/* Bottom Padding for Mobile Nav */}
      <div className="lg:hidden h-24" />
    </div>
  );
};

export default DashboardPage;
